import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import {
  Bus, Users, Snowflake, ShieldCheck, Sparkles, MapPin, Phone,
  MessageCircle, Star, Armchair, Wifi, Luggage, Route, ArrowRight,
} from 'lucide-react';
import { SEOHead, BreadcrumbSchema } from '../components/SEO';

const PRIMARY_PHONE = '+91 6372 350 065';
const WHATSAPP = '916372350065';

const gallery = [
  { src: '/images/fleet/urbania-branded-night.jpg', alt: 'Lovely Sis Tour & Travels Force Urbania luxury van at night', label: 'Signature Night Elegance', span: 'lg:col-span-2 lg:row-span-2' },
  { src: '/images/fleet/urbania-side-day.jpg', alt: 'Force Urbania luxury traveller side profile in daylight', label: 'Panoramic Side Profile', span: '' },
  { src: '/images/fleet/urbania-rear-plate.jpg', alt: 'Force Urbania rear view with roof access ladder', label: 'Spacious Rear & Access', span: '' },
  { src: '/images/fleet/urbania-side-interior.jpg', alt: 'Force Urbania interior cabin with reclining seats and curtains', label: 'Plush Curtained Cabin', span: 'lg:col-span-2' },
];

const specs = [
  { icon: Users, title: '16+1 Seater', desc: 'Spacious luxury seating for groups and families' },
  { icon: Armchair, title: 'Reclining Captain Seats', desc: 'Push-back cushioned seats with ample legroom' },
  { icon: Snowflake, title: 'Dual Climate AC', desc: 'Powerful air-conditioning for every row' },
  { icon: Wifi, title: 'Infotainment & Charging', desc: 'Mood lighting, music system and charging points' },
  { icon: Luggage, title: 'Generous Luggage', desc: 'Dedicated boot and overhead storage space' },
  { icon: ShieldCheck, title: 'Safety First', desc: 'Emergency exit, seatbelts and trained chauffeurs' },
];

const tours = [
  'Deomali Peak Sunrise', 'Duduma Waterfalls', 'Kolab Reservoir',
  'Jagannath Temple Koraput', 'Tribal Haat Markets', 'Coffee Plantations',
  'Airport & Railway Transfers', 'Custom Group Itineraries',
];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

export default function LovelySisPage() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Tours & Travels', url: 'https://thehotelemerald.in/tours' },
  ];

  const whatsappLink = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Hello! I would like to book the Lovely Sis Urbania for a trip around Koraput.')}`;

  return (
    <div className="min-h-screen bg-background" data-testid="lovely-sis-page">
      <SEOHead
        title="Lovely Sis Tour & Travels | Luxury Force Urbania Rental | The Hotel Emerald"
        description="Travel Koraput & the Eastern Ghats in style with Lovely Sis Tour & Travels. Book our premium 16+1 seater Force Urbania luxury van for sightseeing, transfers and group tours from Semiliguda."
        keywords="lovely sis tour and travels, force urbania rental koraput, luxury van hire semiliguda, koraput sightseeing cab, deomali tour package, tempo traveller odisha"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <Navbar />

      {/* ============ CINEMATIC HERO ============ */}
      <section className="relative h-[92vh] min-h-[560px] overflow-hidden" data-testid="lovely-sis-hero">
        <div className="absolute inset-0 z-0">
          <motion.img
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 8, ease: 'easeOut' }}
            src="/images/fleet/urbania-branded-night.jpg"
            alt="Lovely Sis Tour & Travels luxury Urbania"
            className="w-full h-full object-cover"
          />
          {/* Strong scrim for readability */}
          <div className="absolute inset-0 bg-emerald-950/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/40 to-emerald-950/80" />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/85 via-emerald-950/40 to-transparent" />
        </div>

        {/* Floating glow accents */}
        <motion.div
          animate={{ y: [0, -20, 0], opacity: [0.4, 0.7, 0.4] }}
          transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
          className="absolute top-32 right-16 w-72 h-72 bg-gold/15 rounded-full blur-3xl z-10"
        />
        <motion.div
          animate={{ y: [0, 20, 0], opacity: [0.3, 0.6, 0.3] }}
          transition={{ repeat: Infinity, duration: 7, ease: 'easeInOut' }}
          className="absolute bottom-24 left-10 w-80 h-80 bg-emerald-500/15 rounded-full blur-3xl z-10"
        />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-20 h-full flex flex-col justify-center container-custom text-white"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-gold/40 rounded-full mb-6 w-fit" data-testid="lovely-sis-badge">
            <Bus className="w-4 h-4 text-gold" />
            <span className="text-gold text-xs sm:text-sm uppercase tracking-widest font-medium">Official Travel Partner · Semiliguda</span>
          </div>

          <p className="overline text-gold mb-3">Lovely Sis Tour &amp; Travels</p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-tight max-w-3xl mb-5 drop-shadow-lg">
            Explore Koraput in <span className="text-emerald-300">Luxurious</span> Comfort
          </h1>

          <p className="text-emerald-100/90 text-base sm:text-lg max-w-2xl mb-8 drop-shadow">
            Discover the Eastern Ghats aboard our premium <strong className="text-white">Force Urbania</strong> luxury
            traveller — spacious, chauffeur-driven and curated exclusively for guests of The Hotel Emerald.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex items-center justify-center gap-2" data-testid="lovely-sis-book-btn">
              <MessageCircle className="w-4 h-4" />
              Book This Ride
            </a>
            <a href={`tel:${PRIMARY_PHONE.replace(/\s/g, '')}`} className="btn-emerald bg-white/10 border border-white/30 hover:bg-white/20 inline-flex items-center justify-center gap-2" data-testid="lovely-sis-call-btn">
              <Phone className="w-4 h-4" />
              Call to Reserve
            </a>
          </div>
        </motion.div>
      </section>

      {/* ============ OVERVIEW ============ */}
      <section className="section-padding">
        <div className="container-custom grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">The Vehicle</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-5">
              The Force Urbania — A Class Apart
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-5">
              Engineered for discerning travellers, our Urbania pairs a commanding monocoque build with a whisper-quiet
              cabin. Recline into plush captain seats, draw the curtains, and watch the misty hills of the Eastern Ghats
              roll by in complete serenity. Whether it is a sunrise run to Deomali or a seamless airport transfer, every
              journey with Lovely Sis Tour &amp; Travels is designed around your comfort.
            </p>
            <div className="flex flex-wrap gap-2 mb-8">
              {['16+1 Seater', 'Monocoque Luxury', 'Chauffeur Driven', 'Fully Air-Conditioned'].map((tag) => (
                <span key={tag} className="text-xs bg-muted px-3 py-1.5 rounded-full text-foreground">{tag}</span>
              ))}
            </div>
            <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}</div>
              <span className="text-sm text-muted-foreground">Trusted by Hotel Emerald guests across Koraput</span>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="relative">
            <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-2xl">
              <motion.img
                whileHover={{ scale: 1.06 }}
                transition={{ duration: 0.8 }}
                src="/images/fleet/urbania-side-day.jpg"
                alt="Force Urbania side profile"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 w-24 h-24 bg-gold flex flex-col items-center justify-center rounded-sm shadow-xl">
              <Bus className="w-8 h-8 text-emerald-950" />
              <span className="text-[10px] font-semibold text-emerald-950 mt-1">URBANIA</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ SPECS GRID ============ */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">On Board</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">Comfort in Every Detail</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {specs.map((spec, i) => (
              <motion.div
                key={spec.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -6 }}
                className="bg-card border border-border rounded-sm p-6 group"
                data-testid={`spec-card-${i}`}
              >
                <div className="w-12 h-12 rounded-sm bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center mb-4 group-hover:bg-gold transition-colors duration-300">
                  <spec.icon className="w-6 h-6 text-emerald-700 dark:text-emerald-400 group-hover:text-emerald-950 transition-colors duration-300" />
                </div>
                <h3 className="font-serif text-xl text-foreground mb-2">{spec.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{spec.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ GALLERY ============ */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Gallery</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">Meet the Urbania</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:auto-rows-[220px]">
            {gallery.map((img, i) => (
              <motion.div
                key={img.src}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className={`relative group overflow-hidden rounded-sm ${img.span}`}
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full min-h-[220px] object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 p-5 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="text-white font-serif text-lg">{img.label}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TOURS / DESTINATIONS ============ */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp} className="order-2 lg:order-1">
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Where We Go</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">Curated Journeys Across Koraput</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {tours.map((t, i) => (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 bg-card border border-border rounded-sm px-4 py-3"
                >
                  <Route className="w-4 h-4 text-gold flex-shrink-0" />
                  <span className="text-sm text-foreground">{t}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="relative order-1 lg:order-2">
            <div className="aspect-[3/4] max-w-sm mx-auto rounded-sm overflow-hidden shadow-2xl">
              <img src="/images/fleet/urbania-rear-plate.jpg" alt="Force Urbania rear" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="section-padding bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-gold/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>
        <div className="container-custom relative z-10 text-center max-w-3xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-gold text-sm font-medium">Reserve Your Journey</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-4">
              Ready to Ride in <span className="text-gold">Style?</span>
            </h2>
            <p className="text-emerald-200 text-lg mb-8">
              Let our team at The Hotel Emerald arrange your Lovely Sis Urbania for sightseeing, transfers or a
              bespoke group tour across the Eastern Ghats.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex items-center justify-center gap-2" data-testid="cta-whatsapp-btn">
                <MessageCircle className="w-4 h-4" />
                WhatsApp Us
              </a>
              <a href="/contact" className="btn-emerald bg-white/10 border border-white/30 hover:bg-white/20 inline-flex items-center justify-center gap-2" data-testid="cta-contact-btn">
                <MapPin className="w-4 h-4" />
                Enquire Now
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
